import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit2, Trash2, User, Calendar, Tag, Briefcase } from 'lucide-react';
import { format } from 'date-fns';

const TaskItem = ({ task, onEdit, onDelete }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const getStatusColor = (statusName) => {
    switch (statusName?.toLowerCase()) {
      case 'in progress': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'done': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'blocked': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'qa': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'to do': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'backlog': return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
      default: return 'bg-slate-600/30 text-slate-300 border-slate-600/50';
    }
  };

  return (
    <motion.div variants={cardVariants}>
      <Card className="bg-slate-800/70 border-slate-700/50 shadow-xl hover:shadow-blue-500/20 transition-shadow duration-300">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <CardTitle className="text-xl text-slate-100">{task.name}</CardTitle>
            <div className={`text-xs font-semibold px-2 py-1 rounded-full border ${getStatusColor(task.custom_statuses?.name)}`}>
              {task.custom_statuses?.name || 'N/A'}
            </div>
          </div>
          {task.description && (
            <CardDescription className="text-slate-400 pt-1 line-clamp-2">{task.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center text-slate-400">
            <Briefcase size={16} className="mr-2 text-blue-400" />
            Project: <span className="text-slate-200 ml-1">{task.projects?.name || 'N/A'}</span>
          </div>
          <div className="flex items-center text-slate-400">
            <User size={16} className="mr-2 text-green-400" />
            Assignee: <span className="text-slate-200 ml-1">{task.employees?.name || 'Unassigned'}</span>
          </div>
          <div className="flex items-center text-slate-400">
            <Calendar size={16} className="mr-2 text-purple-400" />
            Due Date: <span className="text-slate-200 ml-1">{task.due_date ? format(new Date(task.due_date), 'PPP') : 'Not set'}</span>
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => onEdit(task)} className="text-blue-400 border-blue-400/50 hover:bg-blue-400/10 hover:text-blue-300">
              <Edit2 size={16} className="mr-1" /> Edit
            </Button>
            <Button variant="destructive" size="sm" onClick={() => onDelete(task.id)} className="bg-red-500/80 hover:bg-red-500">
              <Trash2 size={16} className="mr-1" /> Delete
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default TaskItem;